import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// These 2 slugs are NOT used in the main Collections bento grid.
// Main grid uses: south-indian-traditional, hipchains, anti-tarnish, earrings, natural-stones
const FEATURED_SLUGS = ['bangles', 'rings'];

const FeaturedTwoCollections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/collections`).then(r => r.json()),
      fetch(`${API}/products?active=true`).then(r => r.json()),
    ])
      .then(([colData, prodData]) => {
        if (colData.success) {
          const products = prodData.success ? prodData.products : [];

          const countMap = {};
          products.forEach(p => {
            if (Array.isArray(p.collection_slugs)) {
              p.collection_slugs.forEach(slug => {
                countMap[slug] = (countMap[slug] || 0) + 1;
              });
            }
          });

          const enriched = FEATURED_SLUGS
            .map(slug => {
              const col = colData.collections.find(c => c.slug === slug);
              if (!col) return null;
              return { ...col, productCount: countMap[slug] || 0 };
            })
            .filter(Boolean);

          setCollections(enriched);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');

        /* ── Section ── */
        .ftc-section {
          background: #F0EDE6;
          padding: 90px 0 80px;
        }

        /* ── Header ── */
        .ftc-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          padding: 0 clamp(24px, 5vw, 80px);
          margin-bottom: 48px;
          flex-wrap: wrap;
          gap: 20px;
        }
        .ftc-label {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .ftc-label::before {
          content: '';
          width: 28px;
          height: 1px;
          background: #B08D57;
          display: inline-block;
        }
        .ftc-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(30px, 4vw, 48px);
          font-weight: 700;
          color: #1C1A18;
          line-height: 1.08;
          letter-spacing: -0.01em;
        }
        .ftc-title em {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }
        .ftc-viewall {
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #fff;
          background: #1C1A18;
          text-decoration: none;
          padding: 14px 32px;
          display: inline-block;
          transition: background 0.22s;
          white-space: nowrap;
        }
        .ftc-viewall:hover { background: #B08D57; }

        /* ── Two-card grid ── */
        .ftc-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          padding: 0 clamp(24px, 5vw, 80px);
        }

        /* ── Card ── */
        .ftc-card {
          position: relative;
          overflow: hidden;
          cursor: pointer;
          display: block;
          text-decoration: none;
          height: 520px;
        }

        .ftc-card img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.7s ease;
          display: block;
        }

        .ftc-card-placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #2a2620, #1C1A18);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ftc-card:hover img { transform: scale(1.06); }

        /* gradient overlay */
        .ftc-card::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to top,
            rgba(28,26,24,0.80) 0%,
            rgba(28,26,24,0.15) 50%,
            transparent 100%
          );
          z-index: 1;
          transition: background 0.3s;
        }
        .ftc-card:hover::after {
          background: linear-gradient(
            to top,
            rgba(28,26,24,0.90) 0%,
            rgba(28,26,24,0.30) 65%,
            transparent 100%
          );
        }

        /* top-left badge */
        .ftc-badge {
          position: absolute;
          top: 22px;
          left: 22px;
          z-index: 3;
          font-family: 'Jost', sans-serif;
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #1C1A18;
          background: #D4AF78;
          padding: 5px 12px;
        }

        /* top-right index */
        .ftc-number {
          position: absolute;
          top: 22px;
          right: 22px;
          z-index: 3;
          font-family: 'Playfair Display', serif;
          font-size: 11px;
          font-weight: 400;
          font-style: italic;
          color: rgba(212,175,120,0.5);
          letter-spacing: 0.05em;
        }

        /* bottom content */
        .ftc-card-content {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 2;
          padding: 32px;
        }

        /* piece count */
        .ftc-count {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #D4AF78;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .ftc-count-dot {
          width: 4px;
          height: 4px;
          background: #B08D57;
          border-radius: 50%;
          display: inline-block;
        }

        .ftc-name {
          font-family: 'Playfair Display', serif;
          font-size: clamp(22px, 2.5vw, 32px);
          font-weight: 700;
          color: #fff;
          line-height: 1.15;
          margin-bottom: 8px;
        }

        .ftc-desc {
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          font-weight: 300;
          color: rgba(255,255,255,0.65);
          letter-spacing: 0.06em;
          line-height: 1.5;
          max-height: 0;
          overflow: hidden;
          opacity: 0;
          transition: max-height 0.35s ease, opacity 0.3s;
        }
        .ftc-card:hover .ftc-desc {
          max-height: 60px;
          opacity: 1;
        }

        .ftc-shop-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #D4AF78;
          text-decoration: none;
          margin-top: 14px;
          border-bottom: 1px solid rgba(212,175,120,0.4);
          padding-bottom: 2px;
          opacity: 0;
          transform: translateY(6px);
          transition: opacity 0.3s 0.05s, transform 0.3s 0.05s, border-color 0.2s;
        }
        .ftc-card:hover .ftc-shop-link {
          opacity: 1;
          transform: translateY(0);
        }
        .ftc-shop-link:hover { border-color: #D4AF78; color: #fff; }

        /* ── Skeleton ── */
        .ftc-skeleton {
          background: linear-gradient(90deg, #e8e2d9 25%, #f0ebe3 50%, #e8e2d9 75%);
          background-size: 200% 100%;
          animation: ftc-shimmer 1.4s infinite;
          height: 520px;
        }
        @keyframes ftc-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* ── Mobile ── */
        @media (max-width: 768px) {
          .ftc-grid {
            grid-template-columns: 1fr;
          }
          .ftc-card { height: 340px; }
          .ftc-skeleton { height: 340px; }
        }
        @media (max-width: 480px) {
          .ftc-section { padding: 60px 0 52px; }
          .ftc-card { height: 280px; }
          .ftc-skeleton { height: 280px; }
        }
      `}</style>

      <section className="ftc-section">

        {/* ── Header ── */}
        <div className="ftc-header">
          <div>
            <p className="ftc-label">Discover</p>
            <h2 className="ftc-title">
              Featured <em>Picks</em>
            </h2>
          </div>
          <Link to="/collections" className="ftc-viewall">
            View All Collections
          </Link>
        </div>

        {/* ── Grid ── */}
        {loading ? (
          <div className="ftc-grid">
            <div className="ftc-skeleton" />
            <div className="ftc-skeleton" />
          </div>
        ) : collections.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '80px',
            fontFamily: "'Jost', sans-serif",
            fontSize: '13px',
            color: '#9A9186',
            letterSpacing: '0.1em',
          }}>
            No featured collections yet.
          </div>
        ) : (
          <div className="ftc-grid">
            {collections.map((col, i) => (
              <motion.div
                key={col.id}
                className="ftc-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.65,
                  delay: i * 0.12,
                  ease: [0.22, 1, 0.36, 1],
                }}
                viewport={{ once: true, amount: 0.2 }}
              >
                {/* Full-card link */}
                <Link
                  to={`/collections/${col.slug}`}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    inset: 0,
                    zIndex: 3,
                  }}
                  aria-label={col.name}
                />

                {/* Badge (from DB badge column) */}
                {col.badge && (
                  <span className="ftc-badge">{col.badge}</span>
                )}

                {/* Index number */}
                <span className="ftc-number">0{i + 1}</span>

                {/* Image or placeholder */}
                {col.image_url
                  ? <img src={col.image_url} alt={col.name} loading="lazy" />
                  : <div className="ftc-card-placeholder" />
                }

                {/* Bottom content */}
                <div className="ftc-card-content">
                  <div className="ftc-count">
                    <span className="ftc-count-dot" />
                    {col.productCount}{' '}
                    {col.productCount === 1 ? 'piece' : 'pieces'}
                  </div>
                  <div className="ftc-name">{col.name}</div>
                  {col.sub && (
                    <div className="ftc-desc">{col.sub}</div>
                  )}
                  <span className="ftc-shop-link">Shop Now →</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}

      </section>
    </>
  );
};

export default FeaturedTwoCollections;